import { ChatFileSchemaHandlers } from "models/OrganizationModels/OrganizationMetadataConfiguration";
import {
    AdvancedType,
    ConcatFieldType,
    ConcatFieldValueType,
    ConditionRuleAPIType,
    ConditionRuleType,
    CustomFieldType,
    DataSourceType,
    MappedValueType,
    SelectedTargetFields,
    SetupType,
    SourceFieldConfiguration,
    SourceFieldType,
    SourceInfo,
    StatementAPIType,
    StatementType,
    TargetFieldType,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import { ConditionalRuleType, OrganizationMetadataFileType } from "./constants";
import {
    emptyCondition,
    emptyKeyField,
    emptyMappedValue,
    emptySourceField,
} from "./emptyStates";
import { OrganizationMetadataFileTypeExtension } from "models/OrganizationModels/OrganizationMetadataFileTypeExtension";

const formatStatementFromApi = (
    statement?: StatementAPIType,
    level = 1,
): StatementType | undefined => {
    if (!statement) {
        return undefined;
    }

    return {
        ...statement,
        result: statement.concatRule?.literalValue,
        sourceName: statement.concatRule?.sourceName || undefined,
        isLiteral: statement.concatRule?.isLiteral,
        level,
        then: formatStatementFromApi(statement.then, level + 1),
        else: formatStatementFromApi(statement.else, level + 1),
        hasError: false,
    };
};

const updateSourceFieldsFromPath = (
    sourceFields: SourceFieldType[],
    sourceInfo: SourceInfo,
) => {
    const {valuePath, id, sourceName, isRequired, targetFieldName, extractionPattern, } =
        sourceInfo;

    const existingField = sourceFields.find(
        (field) => field.alias === sourceName,
    );
    const updatedField = {
        id: id,
        targetFieldName,
        path: valuePath,
        isRequired: isRequired || false,
        alias: sourceName,
        extractionPattern:
            extractionPattern ?? existingField?.extractionPattern ?? null,
    };
    if (valuePath && !existingField) {
        // valuePath doesn't exist in sourceFields
        sourceFields.push(updatedField);
    }
};

const getSourcesFromStatement = (
    sourceFields: SourceFieldType[],
    statement: StatementAPIType | ConditionRuleAPIType | undefined,
    targetFieldName: string,
) => {
    if (!statement) {
        return sourceFields;
    }

    if (statement.conditionIndicators) {
        for (let i = 0; i < statement.conditionIndicators.length; i++) {
            const indicator = statement.conditionIndicators[i];
            updateSourceFieldsFromPath(sourceFields, {
                valuePath: indicator.valuePath || "",
                id: indicator.id,
                targetFieldName,
                sourceName: indicator.sourceName || "",
                isRequired: indicator.isRequired,
                extractionPattern: indicator.extractionPattern ?? null,
            });
        }
    }

    let concatTerm = statement.concatRule;
    while (concatTerm) {
        if (!concatTerm.isLiteral && concatTerm.expressionValue) {
            updateSourceFieldsFromPath(sourceFields, {
                valuePath: concatTerm.expressionValue,
                id: concatTerm.id,
                targetFieldName: "",
                sourceName: concatTerm.sourceName || "",
            });
        }
        concatTerm = concatTerm.rightTerm;
    }

    getSourcesFromStatement(sourceFields, statement.then, targetFieldName);
    getSourcesFromStatement(sourceFields, statement.else, targetFieldName);

    return sourceFields;
};

export const formatSourceFieldsFromData = (
    datasource: DataSourceType,
    customTargetFields: CustomFieldType[],
    targetFields: TargetFieldType[],
) => {
    const isConcatField = (field: SourceFieldConfiguration) =>
        field.conditionalRuleType === ConditionalRuleType.Concatenation ||
        field.conditionRule?.concatRule?.rightTerm ||
        field.conditionRule?.then?.concatRule?.rightTerm;

    const isMappedValue = (field: SourceFieldConfiguration) =>
        field.conditionalRuleType === ConditionalRuleType.ValueAssignment;

    const isConditionalField = (field: SourceFieldConfiguration) =>
        field.conditionalRuleType === ConditionalRuleType.Conditional ||
        (field.conditionRule && !isConcatField(field) && !isMappedValue(field));

    // custom fields that exist in the config but not in the org's classifier table yet (for implementation or promotion of configs from stage to prod)
    const unresolvedCustomFields: CustomFieldType[] = [];

    const selectedTargetFields: SelectedTargetFields =
        datasource.sourceFieldConfigurations.map((field) => {
            if (field.isCustomField) {
                const customField = customTargetFields.find(
                    (f) => f.label === field.targetFieldName,
                );
                if (customField) {
                    return {
                        targetFieldId: customField.id,
                        sourceFieldId: field.id,
                    };
                }
                // Keep a placeholder target for standalone source fields that do not map to a target.
                if (!field.targetFieldName) {
                    return {
                        targetFieldId: uuidv4(),
                        sourceFieldId: field.id,
                    };
                }
                const syntheticId = uuidv4();
                unresolvedCustomFields.push({
                    id: syntheticId,
                    label: field.targetFieldName,
                    value: field.targetFieldName,
                    isIndexed: field.isIndexed ?? false,
                });
                return {
                    targetFieldId: syntheticId,
                    sourceFieldId: field.id,
                };
            } else {
                const targetField = targetFields.find(
                    (f) => f.label === field.targetFieldName,
                );
                return {
                    targetFieldId: targetField?.id || uuidv4(),
                    sourceFieldId: field.id,
                };
            }
        });

    const sourceFields: SourceFieldType[] = datasource.sourceFieldConfigurations
        .filter(
            (f) =>
                !isConcatField(f) &&
                !isConditionalField(f) &&
                !isMappedValue(f),
        )
        .map((field) => ({
            isRequired: field.isRequired,
            alias: field.sourceName,
            id: field.id,
            extractionPattern: field.extractionPattern ?? null,
            path: field.expressionValuePath || "",
            targetFieldName: field.targetFieldName,
        }));

    let sourcesFromConditions: SourceFieldType[] = [];

    // Get source fields from conditions
    datasource.sourceFieldConfigurations.forEach((field) => {
        if (field.conditionRule) {
            sourcesFromConditions = getSourcesFromStatement(
                sourcesFromConditions,
                field.conditionRule,
                field.targetFieldName,
            );
        }
    });

    sourcesFromConditions.forEach((field) => {
        const existingFieldIndex = sourceFields.findIndex(
            (sourceField) => sourceField.alias === field.alias,
        );
        if (existingFieldIndex === -1) {
            // If a source field comes from conditions, drop the target field
            // The target is handled in the condition
            sourceFields.push({ ...field, targetFieldName: "" });
            return;
        }

        const existingField = sourceFields[existingFieldIndex];
        sourceFields[existingFieldIndex] = {
            ...field,
            ...existingField,
            path: existingField.path || field.path,
            isRequired: existingField.isRequired || field.isRequired,
            extractionPattern:
                existingField.extractionPattern ??
                field.extractionPattern ??
                null,
        };
    });

    const concatFields: ConcatFieldType[] = datasource.sourceFieldConfigurations
        .filter(isConcatField)
        .map((field) => {
            const concatRule =
                field.conditionRule?.concatRule ??
                field.conditionRule?.then?.concatRule;
            const fields: ConcatFieldValueType[] = [];
            let currentRule = concatRule;

            while (currentRule) {
                const currentTerm = currentRule; // This is to avoid a linter error on line 124 referencing an 'unsafe' variable
                if (currentTerm.expressionValue) {
                    fields.push({
                        id: currentTerm.id,
                        type: "field",
                        value: currentTerm.sourceName || "",
                    });
                    updateSourceFieldsFromPath(sourceFields, {
                        valuePath: currentTerm.expressionValue,
                        id: currentTerm.id,
                        targetFieldName: "",
                        sourceName: currentTerm.sourceName || "",
                    });
                } else {
                    fields.push({
                        id: currentTerm.id,
                        type: "separator",
                        value: currentTerm.literalValue || "",
                    });
                }
                currentRule = currentTerm.rightTerm;
            }
            return {
                id: field.id,
                newName: field.sourceName,
                targetFieldName: field.targetFieldName,
                fields,
                hasError: false,
            };
        });

    const mappedValues: MappedValueType[] = datasource.sourceFieldConfigurations
        .filter(isMappedValue)
        .map((field) => {
            const conditionRule = field.conditionRule;
            if (!conditionRule) {
                // This will never happen but added for the linter
                return {
                    id: uuidv4(),
                    targetField: "",
                    pairs: [],
                };
            }
            const mappedValue: MappedValueType = {
                id: field.id,
                targetField: field.targetFieldName,
                pairs: [],
            };

            let currentStatement:
                | StatementAPIType
                | ConditionRuleAPIType
                | undefined = conditionRule;
            while (currentStatement) {
                const conditionIndicators =
                    currentStatement.conditionIndicators;

                if (conditionIndicators) {
                    mappedValue.pairs.push({
                        id: currentStatement.id,
                        sourceValue: conditionIndicators[0].value,
                        targetValue:
                            currentStatement.then?.concatRule?.literalValue ||
                            "",
                    });
                }
                currentStatement = currentStatement.else;
            }
            if (!mappedValue.pairs.length) {
                mappedValue.pairs = emptyMappedValue().pairs;
            }
            return mappedValue;
        });

    const conditionRules: ConditionRuleType[] =
        datasource.sourceFieldConfigurations
            .filter((f) => isConditionalField(f))
            .map((field) => {
                const conditionRule = field.conditionRule;
                if (!conditionRule) {
                    // This will never happen but added for the linter
                    return emptyCondition();
                }
                const updatedConditionRule: ConditionRuleType = {
                    id: field.id,
                    targetFieldName: field.targetFieldName,
                    conditionIndicators: conditionRule.conditionIndicators,
                    then: formatStatementFromApi(
                        conditionRule.then,
                    ) as StatementType,
                    else: formatStatementFromApi(conditionRule.else),
                    hasError: false,
                    hasTargetFieldError: false,
                };

                return updatedConditionRule;
            });

    let keyFields = [emptyKeyField()];
    const associateFields =
        datasource.metadataExtendedAssociation.associationFields;
    if (associateFields.length) {
        keyFields = associateFields.map(
            ({ sourceFieldName, targetFieldName }) => ({
                id: uuidv4(),
                sourceFieldName,
                targetFieldName,
            }),
        );
        // For now if the targetFieldName matches both core and extended, it will use the core field
        selectedTargetFields.push(
            ...keyFields.map(({ targetFieldName, id }) => {
                const foundCoreField = targetFields.find(
                    (field) => field.label === targetFieldName,
                );

                const foundCustomField = customTargetFields.find(
                    (field) => field.label === targetFieldName,
                );

                return {
                    sourceFieldId: id,
                    targetFieldId:
                        foundCoreField?.id || foundCustomField?.id || uuidv4(),
                };
            }),
        );
    }

    if (sourceFields.length === 0) {
        return {
            sourceFields: [emptySourceField()],
            conditionRules: [],
            concatFields: [],
            selectedTargetFields: [],
            mappedValues: [emptyMappedValue()],
            keyFields,
            unresolvedCustomFields,
        };
    } else {
        return {
            sourceFields,
            conditionRules,
            concatFields,
            selectedTargetFields,
            mappedValues: mappedValues.length
                ? mappedValues
                : [emptyMappedValue()],
            keyFields,
            unresolvedCustomFields,
        };
    }
};

export const getSetupFromData = (datasource: DataSourceType) => {
    const fileTypeExtension =
        OrganizationMetadataFileTypeExtension[
            datasource.metaDataFileTypeExtension || 0
        ];
    const chatHandler = datasource.chatFileSchemaHandler;
    return {
        chatFileSchemaHandler:
            typeof chatHandler === "number"
                ? ChatFileSchemaHandlers[chatHandler]
                : null,
        isDebug: datasource.isDebug || false,
        namespace: datasource.nameSpace || "",
        prefix: datasource.prefix || "",
        isAgentCodeRequired: datasource.agentCodeRequired || false,
        configurationName: datasource.name || "",
        fileType:
            OrganizationMetadataFileType[datasource.metaDataFileType || 0],
        fileTypeExtension,
        sourceType: fileTypeExtension.includes("_EXT") ? "batch" : "single",
        delimiter: datasource.csvProperties?.delimiter || "",
    } as SetupType;
};

export const getAdvancedFromData = (
    datasource: DataSourceType,
): AdvancedType => ({
    campaignIndicatorDefinitions: datasource.campaignIndicatorDefinitions
        ?.length
        ? datasource.campaignIndicatorDefinitions.map((definition) => {
              return {
                  id: definition.id,
                  campaignId: definition.campaignId,
                  fieldPath: definition.fieldPath,
                  comparisonOperator: definition.comparisonOperator,
                  campaignIndicatorStrings:
                      definition.campaignIndicatorStrings.map((indicator) => ({
                          id: uuidv4(),
                          value: indicator,
                      })),
              };
          })
        : undefined,
    languageIndicatorDefinitions: datasource.languageIndicatorDefinitions
        ?.length
        ? datasource.languageIndicatorDefinitions.map((definition) => {
              return {
                  id: definition.id,
                  localeId: definition.localeId,
                  fieldPath: definition.fieldPath,
                  comparisonOperator: definition.comparisonOperator,
                  languageIndicatorStrings:
                      definition.languageIndicatorStrings.map((indicator) => ({
                          id: uuidv4(),
                          value: indicator,
                      })),
              };
          })
        : undefined,
    mediaSegments: datasource.mediaSegments
        ? {
              start: datasource.mediaSegments.start.valuePath || "",
              endPrimary: datasource.mediaSegments.end.primary.valuePath || "",
              endAlternate:
                  datasource.mediaSegments.end.alternate.valuePath || "",
              agentName: datasource.mediaSegments.agentName.valuePath || "",
              agentId: datasource.mediaSegments.agentId.valuePath || "",
              rootPath: datasource.mediaSegments.rootPath,
          }
        : undefined,
    dateFormatPatterns: datasource.customDateFormatPatterns?.length
        ? datasource.customDateFormatPatterns.map((pattern) => ({
              pattern,
              id: uuidv4(),
          }))
        : [],
});
